/**
 * 上映スケジュールAI ステップ3：タイムチャート作成の画面状態と API 操作（DEV-14983）
 * 対象期間は dateFrom〜dateTo（1〜14 日、開始曜日は自由）。保存済みの期間と重なってもよいが、
 * 重なる日（排班済みの日）の上映回は AI 生成・上映回の保存時に確認のうえ上書きする（overwriteOverlap）。
 *
 * index.vue で createPlanState() を作って provide し、子コンポーネントは usePlanState() で受け取る。
 * 入力・ルール・上映回はそれぞれ別の API で保存するため、未保存変更もそれぞれスナップショットで判定する。
 * ルールの判定・違反チェックはすべてバックエンドが行い、フロントでは計算しない（終了時刻の表示計算のみ）。
 */
import { ref, computed, watch, provide, inject, h } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { checkPermi } from "@/utils/permission";
import {
  getScheduleAiPlanService,
  saveScheduleAiPlanInputService,
  parseScheduleAiPlanRulesService,
  saveScheduleAiPlanRulesService,
  generateScheduleAiPlanService,
  getScheduleAiPlanJobService,
  validateScheduleAiPlanService,
  saveScheduleAiPlanShowsService,
  deleteScheduleAiPlanService,
} from "@/api/dp/index.js";
import {
  colorKey,
  paletteColor,
  periodDates,
  periodLabel,
  periodRangeLabel,
  prereqStepOf,
  refreshShowTimes,
  toShowPayload,
  VIOLATION_TYPE_LABELS,
} from "./utils";

const PLAN_STATE_KEY = Symbol("scheduleAiPlanState");
// 編集権限
export const PLAN_EDIT_PERMI = "dp:scheduleAi:plan:edit";
// AI 生成ジョブのポーリング間隔（ミリ秒）
const POLL_INTERVAL = 3000;
// ポーリングの通信エラーが続いたら打ち切る回数
const POLL_MAX_ERRORS = 3;
// チェック API のデバウンス（ミリ秒）
const VALIDATE_DEBOUNCE = 300;
// ページ再読み込み後も生成中ジョブを追えるよう、期間ごとに jobId を sessionStorage に残す
const JOB_STORAGE_PREFIX = "scheduleAiPlanJob:";
// 保存済みの期間と重なっていて上書きの確認が済んでいない（バックエンドのエラーコード）
const CODE_OVERLAP = 4604;
// 既存期間の途中だけを上書きすることになる（既存期間が 2 つに分かれるため不可）
const CODE_OVERLAP_SPLIT = 4605;
// 権限不足（403）のときに添える案内（ログイン後に権限が付与された場合は再ログインが必要）
const PERMISSION_HINT = "権限が更新された場合は再ログインしてください";

/**
 * 画面に残すエラーメッセージ。403 のときは再ログインの案内を添える
 */
const errorText = (e, fallback) => {
  const msg = e?.message || fallback;
  return e?.code === 403 ? `${msg}（${PERMISSION_HINT}）` : msg;
};

// v-for のキー用ローカル連番（新規行は showId / ruleId が null のため使えない）
let localKeySeq = 0;
const nextKey = (prefix) => {
  localKeySeq += 1;
  return `${prefix}-${localKeySeq}`;
};

/**
 * sessionStorage の読み書き（プライベートモード等で例外になっても画面は動かす）
 */
// key は期間キー（periodKey）
const jobStorage = {
  get(key) {
    try {
      return sessionStorage.getItem(JOB_STORAGE_PREFIX + key);
    } catch {
      return null;
    }
  },
  set(key, jobId) {
    try {
      sessionStorage.setItem(JOB_STORAGE_PREFIX + key, jobId);
    } catch {
      // 保存できなくても生成自体は続ける
    }
  },
  remove(key) {
    try {
      sessionStorage.removeItem(JOB_STORAGE_PREFIX + key);
    } catch {
      // 何もしない
    }
  },
};

/**
 * API の目標回数行を編集行に変換
 */
export const toTargetRow = (t = {}) => ({
  _key: nextKey("target"),
  schedMovieId: t.schedMovieId ?? null,
  versionId: t.versionId ?? null,
  dateFrom: t.dateFrom || null,
  dateTo: t.dateTo || null,
  count: t.count ?? undefined,
  countUnit: t.countUnit || "TOTAL",
});

/**
 * API のルールを編集行に変換
 */
export const toRuleRow = (r = {}) => ({
  _key: nextKey("rule"),
  ruleId: r.ruleId ?? null,
  source: r.source || "CINEMA",
  kind: r.kind || "OTHER",
  schedMovieId: r.schedMovieId ?? null,
  versionId: r.versionId ?? null,
  dateFrom: r.dateFrom || null,
  dateTo: r.dateTo || null,
  count: r.count ?? null,
  countUnit: r.countUnit || null,
  timeFrom: r.timeFrom || null,
  timeTo: r.timeTo || null,
  screenId: r.screenId ?? null,
  // 配分指定（SHARE_PREF）の目安割合 %
  shareRatio: r.shareRatio ?? null,
  hard: !!r.hard,
  resolve: r.resolve || "RESOLVED",
  excerpt: r.excerpt || "",
  description: r.description || "",
  // 解析できなかった理由など利用者向けの補足（rules/parse のみ返る。保存しない）
  hint: r.hint || "",
});

/**
 * API の上映回を編集行に変換（終了時刻はバックエンドの値がなければ計算する）
 */
export const toShowRow = (s = {}) => {
  const row = {
    _key: nextKey("show"),
    showId: s.showId ?? null,
    screenId: s.screenId,
    schedMovieId: s.schedMovieId,
    versionId: s.versionId,
    bizDate: s.bizDate,
    startTime: s.startTime,
    adMinute: s.adMinute ?? 0,
    trailerMinute: s.trailerMinute ?? 0,
    bufferMinute: s.bufferMinute ?? 0,
    featureMinute: s.featureMinute ?? 0,
    endTime: s.endTime || "",
    occupyEndTime: s.occupyEndTime || "",
    estSeats: s.estSeats ?? null,
    source: s.source || "MANUAL",
  };
  if (!row.endTime || !row.occupyEndTime) refreshShowTimes(row);
  return row;
};

const emptyCheck = () => ({ compliance: null, violations: [], ruleChecks: [] });

/**
 * 画面状態を作る（index.vue で 1 回だけ呼ぶ）
 */
export const createPlanState = () => {
  // 編集権限がなければすべて読み取り専用
  const canEdit = checkPermi([PLAN_EDIT_PERMI]);

  // 対象期間（yyyy-MM-dd、両端を含む）
  const dateFrom = ref("");
  const dateTo = ref("");
  // 期間を識別するキー（生成ジョブの保存・期間切り替えの判定に使う）
  const periodKey = computed(() => (dateFrom.value && dateTo.value ? `${dateFrom.value}~${dateTo.value}` : ""));
  // API に渡す期間
  const periodParams = () => ({ dateFrom: dateFrom.value, dateTo: dateTo.value });
  const loading = ref(false);
  // 取得失敗時のメッセージ（ステップ1・2 未登録など）。値があれば本文を出さない
  const loadErrorMsg = ref("");
  // 取得失敗の原因になった前提ステップ（"setting" | "movie" | null）。該当ページへのリンク表示に使う
  const loadErrorStep = ref(null);
  const saved = ref(false);
  // 楽観ロック用のバージョン（作品のバージョンと区別するため planVersion と呼ぶ）
  const planVersion = ref(null);
  const generatedTime = ref(null);
  // この期間と重なっている保存済みの期間（[{ dateFrom, dateTo, compliance, ... }]）。
  // 重なる日の上映回は AI 生成・上映回の保存時に確認のうえ上書きする
  const overlaps = ref([]);

  // ---------- 期間の入力 ----------
  const adMinute = ref(0);
  const trailerMinute = ref(0);
  // スクリーン（マスタ項目＋編集する cleaningMinute）
  const screens = ref([]);
  // 作品（マスタ項目＋編集する priority・distributorText・cinemaRequestText）
  const movies = ref([]);
  const targets = ref([]);

  // ---------- ルール ----------
  const rules = ref([]);
  // 自由記述の保存後にルールが未解析（バックエンド判定）
  const rulesStale = ref(false);

  // ---------- 上映回・チェック ----------
  const shows = ref([]);
  const check = ref(emptyCheck());
  // check.violations[].showIndex を解決するための、チェック時に送った上映回の _key 順
  const checkShowKeys = ref([]);
  const summary = ref(null);
  const estTotalSeats = ref(null);
  const estUnknownCount = ref(0);

  // ---------- 未保存変更の判定 ----------
  // 初回保存（saved=false）は version を送らず、以降は現在の version を必ず送る（楽観ロック）
  const buildInputPayload = () => ({
    ...periodParams(),
    ...(saved.value ? { version: planVersion.value } : {}),
    adMinute: adMinute.value ?? 0,
    trailerMinute: trailerMinute.value ?? 0,
    screens: screens.value.map((s) => ({ screenId: s.screenId, cleaningMinute: s.cleaningMinute ?? 0 })),
    movies: movies.value.map((m) => ({
      schedMovieId: m.schedMovieId,
      priority: m.priority ?? null,
      distributorText: m.distributorText || "",
      cinemaRequestText: m.cinemaRequestText || "",
    })),
    targets: targets.value.map((t) => ({
      schedMovieId: t.schedMovieId,
      versionId: t.versionId ?? null,
      dateFrom: t.dateFrom,
      dateTo: t.dateTo,
      count: t.count ?? null,
      countUnit: t.countUnit,
    })),
  });
  // version は保存のたびに変わるため差分判定から外す
  const inputSignature = () => JSON.stringify({ ...buildInputPayload(), version: null });
  // ルール解析の対象となる自由記述だけの署名
  const textSignature = () => JSON.stringify(
    movies.value.map((m) => [m.schedMovieId, m.distributorText || "", m.cinemaRequestText || ""]),
  );
  // ローカル用の _key・表示用の hint は送らない。新規ルールは ruleId を付けない
  const buildRulesPayload = () => rules.value.map((r) => {
    const rest = { ...r };
    delete rest._key;
    delete rest.hint;
    if (rest.ruleId === null || rest.ruleId === undefined) delete rest.ruleId;
    return rest;
  });
  const showsSignature = () => JSON.stringify(shows.value.map(toShowPayload));

  const inputSnapshot = ref("");
  const textSnapshot = ref("");
  const rulesSnapshot = ref("");
  const showsSnapshot = ref("");
  // 保存済みの上映回があるか（AI 生成時の上書き確認用）
  const hasSavedShows = ref(false);

  const inputDirty = computed(() => !!inputSnapshot.value && inputSignature() !== inputSnapshot.value);
  // 自由記述を保存済みの内容から変えた（ルールの再解析が必要）
  const textsDirty = computed(() => !!textSnapshot.value && textSignature() !== textSnapshot.value);
  const rulesDirty = computed(() => !!rulesSnapshot.value && JSON.stringify(buildRulesPayload()) !== rulesSnapshot.value);
  const showsDirty = computed(() => !!showsSnapshot.value && showsSignature() !== showsSnapshot.value);
  const anyDirty = computed(() => inputDirty.value || rulesDirty.value || showsDirty.value);

  // ---------- 参照用マップ ----------
  const screenMap = computed(() => new Map(screens.value.map((s) => [s.screenId, s])));
  const movieMap = computed(() => new Map(movies.value.map((m) => [m.schedMovieId, m])));
  const versionMap = computed(() => {
    const map = new Map();
    movies.value.forEach((m) => (m.versions || []).forEach((v) => map.set(colorKey(m.schedMovieId, v.versionId), v)));
    return map;
  });
  const ruleMap = computed(() => new Map(rules.value.filter((r) => r.ruleId != null).map((r) => [r.ruleId, r])));
  // 作品＋バージョンの色（ステップ2 の登録順で固定）
  const colorMap = computed(() => {
    const map = new Map();
    let index = 0;
    movies.value.forEach((m) => (m.versions || []).forEach((v) => {
      map.set(colorKey(m.schedMovieId, v.versionId), paletteColor(index));
      index += 1;
    }));
    return map;
  });
  const days = computed(() => periodDates(dateFrom.value, dateTo.value));

  /**
   * 違反が指す上映回の _key
   * showIndex（チェック時に送った / GET で返った shows 配列の添字）を優先し、なければ showId で探す
   */
  const violationShowKey = (v) => {
    if (Number.isInteger(v.showIndex)) {
      const key = checkShowKeys.value[v.showIndex];
      if (key) return key;
    }
    if (v.showId !== null && v.showId !== undefined) {
      return shows.value.find((s) => s.showId === v.showId)?._key ?? null;
    }
    return null;
  };

  // 違反一覧（表示用に対象上映回の _key を付ける）
  const violations = computed(() => (check.value?.violations || []).map((v, i) => ({
    ...v,
    _key: `violation-${i}`,
    showKey: violationShowKey(v),
    typeLabel: VIOLATION_TYPE_LABELS[v.type] || v.type,
  })));
  // 上映回 _key → 違反の配列
  const violationsByShow = computed(() => {
    const map = new Map();
    violations.value.forEach((v) => {
      if (!v.showKey) return;
      if (!map.has(v.showKey)) map.set(v.showKey, []);
      map.get(v.showKey).push(v);
    });
    return map;
  });

  // ---------- 取得 ----------

  /**
   * 取得結果を画面に反映する。keep で指定した部分は未保存の編集を残す
   */
  const applyPeriodData = (data, keep = {}) => {
    saved.value = !!data.saved;
    planVersion.value = data.version ?? null;
    generatedTime.value = data.generatedTime || null;
    overlaps.value = Array.isArray(data.overlaps) ? data.overlaps : [];
    rulesStale.value = !!data.rulesStale;
    summary.value = data.summary || null;
    estTotalSeats.value = data.estTotalSeats ?? null;
    estUnknownCount.value = data.estUnknownCount ?? 0;

    if (!keep.input) {
      adMinute.value = data.adMinute ?? 0;
      trailerMinute.value = data.trailerMinute ?? 0;
      screens.value = (data.screens || []).map((s) => ({ ...s, cleaningMinute: s.cleaningMinute ?? 0 }));
      movies.value = (data.movies || []).map((m) => ({
        ...m,
        priority: m.priority ?? null,
        distributorText: m.distributorText || "",
        cinemaRequestText: m.cinemaRequestText || "",
      }));
      targets.value = (data.targets || []).map(toTargetRow);
      inputSnapshot.value = inputSignature();
      textSnapshot.value = textSignature();
    }
    if (!keep.rules) {
      rules.value = (data.rules || []).map(toRuleRow);
      rulesSnapshot.value = JSON.stringify(buildRulesPayload());
    }
    const serverShows = (data.shows || []).map(toShowRow);
    hasSavedShows.value = serverShows.length > 0;
    if (!keep.shows) {
      shows.value = serverShows;
      showsSnapshot.value = showsSignature();
      check.value = data.check || emptyCheck();
      checkShowKeys.value = shows.value.map((s) => s._key);
      lastValidatedSignature = showsSnapshot.value;
    } else {
      // 上映回の編集を残す場合は、最新の保存状態と比べ直してチェックもやり直す
      showsSnapshot.value = JSON.stringify(serverShows.map(toShowPayload));
      scheduleValidate();
    }
  };

  // 取得の世代（期間を素早く切り替えたとき古い応答で上書きしないため）
  let loadSeq = 0;

  /**
   * 対象期間を取得。keep を渡すと未保存の部分を残したまま再取得する
   */
  const loadPeriod = async (keep = {}) => {
    if (!periodKey.value) return;
    loadSeq += 1;
    const seq = loadSeq;
    const isPartial = keep.input || keep.rules || keep.shows;
    if (!isPartial) loading.value = true;
    loadErrorMsg.value = "";
    loadErrorStep.value = null;
    try {
      const res = await getScheduleAiPlanService(periodParams());
      if (seq !== loadSeq) return;
      applyPeriodData(res?.data || {}, keep);
    } catch (e) {
      if (seq !== loadSeq) return;
      // 部分再取得の失敗は画面を残す（メッセージは request.js でトースト済み）
      if (!isPartial) {
        loadErrorMsg.value = errorText(e, "データの取得に失敗しました");
        loadErrorStep.value = prereqStepOf(loadErrorMsg.value, e?.code);
      }
    } finally {
      if (seq === loadSeq) loading.value = false;
    }
  };

  /**
   * 保存後の再取得。保存していない他の部分の編集は残す
   */
  const reloadKeeping = (savedPart) => loadPeriod({
    input: savedPart !== "input" && inputDirty.value,
    rules: savedPart !== "rules" && rulesDirty.value,
    shows: savedPart !== "shows" && showsDirty.value,
  });

  // ---------- 入力の保存 ----------
  const inputSaving = ref(false);

  /**
   * 入力を検証してエラーメッセージを返す（問題なければ空文字）
   */
  const validateInput = () => {
    const list = days.value;
    for (const m of movies.value) {
      if (!Number.isInteger(m.priority) || m.priority < 1 || m.priority > 10) {
        return `「${m.title}」の優先度を選択してください`;
      }
    }
    for (let i = 0; i < targets.value.length; i += 1) {
      const t = targets.value[i];
      const title = movieMap.value.get(t.schedMovieId)?.title || "";
      const prefix = `「${title}」の目標回数`;
      if (!t.dateFrom || !t.dateTo) return `${prefix}：期間を選択してください`;
      if (t.dateFrom > t.dateTo) return `${prefix}：終了日は開始日以降を選択してください`;
      if (t.dateFrom < list[0] || t.dateTo > list[list.length - 1]) return `${prefix}：期間は対象期間の範囲で選択してください`;
      if (!Number.isInteger(t.count) || t.count < 1) return `${prefix}：回数を1以上の整数で入力してください`;
      if (!t.countUnit) return `${prefix}：単位を選択してください`;
    }
    return "";
  };

  // 重なり方（バックエンドの overlaps[].action）：DELETE＝既存期間をまるごと削除 / TRIM＝既存期間を短くする / SPLIT＝途中だけ上書き（不可）
  const OVERLAP_ACTION_LABELS = { DELETE: "期間ごと削除", TRIM: "重なる日を削除して期間を短縮" };

  /**
   * 重なりの表示（「2026/11/12 〜 11/14（11/13〜11/14 が重なる）」）。重なる日の情報がなければ期間だけ
   */
  const overlapLabelOf = (o) => {
    const range = periodRangeLabel(o.dateFrom, o.dateTo);
    if (!o.overlapDateFrom || !o.overlapDateTo) return range;
    return `${range}（${periodLabel(o.overlapDateFrom, o.overlapDateTo)} が重なる）`;
  };
  // 途中だけを上書きすることになる重なり（既存期間が 2 つに分かれるため保存・生成できない）
  const splitOverlaps = computed(() => overlaps.value.filter((o) => o.action === "SPLIT"));
  // 上書きできる重なり
  const overwriteOverlaps = computed(() => overlaps.value.filter((o) => o.action !== "SPLIT"));
  const overlapLabels = computed(() => overwriteOverlaps.value.map(overlapLabelOf).join("、"));

  /**
   * 途中だけを上書きする重なりがあるときのエラー文（なければ空文字）
   */
  const splitMessage = computed(() => {
    if (!splitOverlaps.value.length) return "";
    const list = splitOverlaps.value.map((o) => periodRangeLabel(o.dateFrom, o.dateTo)).join("、");
    return `既存期間（${list}）の途中だけを上書きすることはできません。先に既存期間を削除するか、期間を選び直してください`;
  });

  /**
   * 保存済みの期間と重なっているときの案内（重なりがなければ空文字）
   */
  const overlapMessage = computed(() => {
    if (splitMessage.value) return splitMessage.value;
    if (!overwriteOverlaps.value.length) return "";
    return `この期間には排班済みの日が含まれています（${overlapLabels.value}）。AIでタイムチャートを作成・保存すると、重なる日の上映回は上書きされます`;
  });

  /**
   * 重なる日の上映回を上書きしてよいか確認する（重なりがなければ確認せず true）
   * 途中だけを上書きする重なりがあれば確認せずエラーを出して false。
   * action は「作成」「保存」など操作名。戻り値：続けてよいか
   */
  const confirmOverwriteOverlap = async (action) => {
    if (splitMessage.value) {
      ElMessage.error(splitMessage.value);
      return false;
    }
    if (!overwriteOverlaps.value.length) return true;
    const detail = overwriteOverlaps.value
      .map((o) => h("li", `${overlapLabelOf(o)}：${OVERLAP_ACTION_LABELS[o.action] || "重なる日を上書き"}`));
    try {
      await ElMessageBox.confirm(
        h("div", [
          h("p", { style: "margin:0 0 8px" }, "以下の保存済みの期間と重なる日の上映回は上書きされ、元に戻せません。"),
          h("ul", { style: "margin:0 0 8px;padding-left:18px;line-height:1.7" }, detail),
          h("p", { style: "margin:0" }, `${action}しますか？`),
        ]),
        "排班済みの日の上書き",
        { confirmButtonText: `上書きして${action}`, cancelButtonText: "キャンセル", type: "warning" },
      );
      return true;
    } catch {
      return false;
    }
  };

  // 解析・生成の前に入力の保存が必要か（未保存の変更がある、またはこの期間がまだ一度も保存されていない）
  const needsInputSave = () => inputDirty.value || !saved.value;

  /**
   * 期間の入力を保存。silent=true のときは成功トーストを出さない（解析・生成の前に自動保存する場合）
   * 戻り値：保存できたか
   */
  const saveInput = async ({ silent = false } = {}) => {
    // 既存期間の途中だけを上書きする期間は保存できない（バックエンドも 4605 で拒否する）
    if (splitMessage.value) {
      ElMessage.error(splitMessage.value);
      return false;
    }
    const error = validateInput();
    if (error) {
      ElMessage.error(error);
      return false;
    }
    inputSaving.value = true;
    try {
      const res = await saveScheduleAiPlanInputService(buildInputPayload());
      // 保存後の期間データ（GET と同じ構造）が返るので再取得せずに反映。未保存のルール・上映回は残す
      applyPeriodData(res?.data || {}, { rules: rulesDirty.value, shows: showsDirty.value });
      if (!silent) ElMessage.success("期間の設定を保存しました");
      return true;
    } catch (e) {
      // エラーメッセージは request.js でトースト済み。楽観ロックの競合なら再読み込みを促す
      await handleConflict(e);
      await reloadOverlapsIfNeeded(e);
      return false;
    } finally {
      inputSaving.value = false;
    }
  };

  // ---------- ルール ----------
  // ルールは読み取り専用のプレビュー。要望の修正はステップ1 に戻って行う
  const rulesParsing = ref(false);
  const rulesSaving = ref(false);
  // 解析の失敗メッセージ（空文字なら失敗していない）。ルール確認で再試行を出すために残す
  const parseError = ref("");

  /**
   * 自由記述をルールに解析（入力が未保存なら先に保存する）。結果はプレビューとして表に出し、確定は saveRules で行う
   * バックエンドは AI 入力のハッシュで結果をキャッシュするため、内容が同じなら何度呼んでも同じ結果が返る
   * 戻り値：解析できたか
   */
  const parseRules = async () => {
    if (needsInputSave() && !(await saveInput({ silent: true }))) return false;
    const key = periodKey.value;
    rulesParsing.value = true;
    parseError.value = "";
    try {
      const res = await parseScheduleAiPlanRulesService(periodParams());
      // 解析中に期間を切り替えた場合は結果を捨てる
      if (key !== periodKey.value) return false;
      const list = Array.isArray(res?.data) ? res.data : [];
      rules.value = list.map(toRuleRow);
      // 解析結果は未保存。空でも確定が必要なため、スナップショットを空ではない別値にしておく
      if (JSON.stringify(buildRulesPayload()) === rulesSnapshot.value) rulesSnapshot.value = "__parsed__";
      return true;
    } catch (e) {
      // エラーメッセージは request.js でトースト済み。画面にも残して再試行できるようにする
      if (key === periodKey.value) parseError.value = errorText(e, "ルールの解析に失敗しました");
      return false;
    } finally {
      if (key === periodKey.value) rulesParsing.value = false;
    }
  };

  /**
   * ルールを確定して保存（プレビューの内容をそのまま全量で送る）
   * silent=true のときは成功トーストを出さない（確定後にそのまま AI 生成する場合）
   */
  const saveRules = async ({ silent = false } = {}) => {
    rulesSaving.value = true;
    try {
      const res = await saveScheduleAiPlanRulesService({ ...periodParams(), rules: buildRulesPayload() });
      // 保存後の期間データ（新しい version を含む）を反映。未保存の入力・上映回は残す
      applyPeriodData(res?.data || {}, { input: inputDirty.value, shows: showsDirty.value });
      if (!silent) ElMessage.success("ルールを確定しました");
      return true;
    } catch (e) {
      await handleConflict(e);
      return false;
    } finally {
      rulesSaving.value = false;
    }
  };

  // ---------- 上映回の編集・チェック ----------
  const validating = ref(false);
  let validateTimer = null;
  let validateSeq = 0;
  // 直近にチェックした（または保存済みの）上映回の署名。同じ内容なら API を呼ばない
  let lastValidatedSignature = "";

  const runValidate = async () => {
    const payloadShows = shows.value.map(toShowPayload);
    const signature = JSON.stringify(payloadShows);
    if (signature === lastValidatedSignature) return;
    lastValidatedSignature = signature;
    const keys = shows.value.map((s) => s._key);
    validateSeq += 1;
    const seq = validateSeq;
    validating.value = true;
    try {
      const res = await validateScheduleAiPlanService({ ...periodParams(), shows: payloadShows });
      if (seq !== validateSeq) return;
      check.value = res?.data || emptyCheck();
      checkShowKeys.value = keys;
    } catch {
      // 失敗時は次の変更で再チェックできるよう署名を戻す
      if (seq === validateSeq) lastValidatedSignature = "";
    } finally {
      if (seq === validateSeq) validating.value = false;
    }
  };

  function scheduleValidate() {
    clearTimeout(validateTimer);
    validateTimer = setTimeout(runValidate, VALIDATE_DEBOUNCE);
  }

  // 上映回が変わるたびにチェックし直す（デバウンス）
  watch(
    () => showsSignature(),
    (signature) => {
      if (!canEdit || !showsSnapshot.value || signature === lastValidatedSignature) return;
      scheduleValidate();
    },
  );

  /**
   * 上映回を追加
   */
  const addShow = (show) => {
    const row = toShowRow({ ...show, showId: null, source: "MANUAL" });
    shows.value.push(row);
    return row;
  };

  /**
   * 上映回の項目を更新（終了時刻も付け直す）
   */
  const updateShow = (key, patch) => {
    const row = shows.value.find((s) => s._key === key);
    if (!row) return;
    Object.assign(row, patch);
    refreshShowTimes(row);
  };

  const removeShow = (key) => {
    const index = shows.value.findIndex((s) => s._key === key);
    if (index !== -1) shows.value.splice(index, 1);
  };

  /**
   * 編集中の上映回を破棄して保存済みの状態に戻す
   */
  const discardShows = () => loadPeriod({ input: inputDirty.value, rules: rulesDirty.value });

  // ---------- 上映回の保存 ----------
  const showsSaving = ref(false);

  /**
   * 違反一覧を確認ダイアログ用の VNode にする（多い場合は先頭だけ）
   */
  const violationListVNode = (list) => {
    const LIMIT = 10;
    const items = list.slice(0, LIMIT).map((v) => h("li", `【${VIOLATION_TYPE_LABELS[v.type] || v.type}】${v.message || ""}`));
    if (list.length > LIMIT) items.push(h("li", `ほか ${list.length - LIMIT}件`));
    return h("div", [
      h("p", { style: "margin:0 0 8px" }, "以下の問題があります。非準拠の下書きとして保存しますか？"),
      h("ul", { style: "margin:0;padding-left:18px;max-height:240px;overflow:auto;line-height:1.7" }, items),
    ]);
  };

  /**
   * 確認後に別の画面で重なる期間が保存された（4604 / 4605）場合は、編集を残したまま重なりの情報を取り直す
   * （次の操作で新しい重なりについて改めて上書きの確認を出すため）
   */
  async function reloadOverlapsIfNeeded(e) {
    if (e?.code !== CODE_OVERLAP && e?.code !== CODE_OVERLAP_SPLIT) return;
    await loadPeriod({ input: inputDirty.value, rules: rulesDirty.value, shows: showsDirty.value });
  }

  /**
   * 楽観ロックの競合（他の画面で保存済み）なら再読み込みを促す
   */
  async function handleConflict(e) {
    if (e?.code !== 409) return;
    try {
      await ElMessageBox.confirm("他の画面で更新されました。再読み込みしてください（未保存の変更は破棄されます）", "確認", {
        confirmButtonText: "再読み込み",
        cancelButtonText: "キャンセル",
        type: "warning",
      });
      await loadPeriod();
    } catch {
      // キャンセル時は何もしない
    }
  }

  /**
   * 上映回を保存。違反があれば確認のうえ非準拠の下書きとして保存し直す
   */
  const saveShows = async () => {
    // 排班済みの日と重なる場合は、上書きの確認を先に取る
    if (!(await confirmOverwriteOverlap("保存"))) return false;
    showsSaving.value = true;
    try {
      const body = {
        ...periodParams(),
        version: planVersion.value,
        confirmNonCompliant: false,
        overwriteOverlap: overwriteOverlaps.value.length > 0,
        shows: shows.value.map(toShowPayload),
      };
      const keys = shows.value.map((s) => s._key);
      let res = await saveScheduleAiPlanShowsService(body);
      if (res?.data && res.data.saved === false) {
        check.value = res.data.check || check.value;
        checkShowKeys.value = keys;
        try {
          await ElMessageBox.confirm(violationListVNode(res.data.check?.violations || []), "確認", {
            confirmButtonText: "非準拠の下書きとして保存",
            cancelButtonText: "キャンセル",
            type: "warning",
          });
        } catch {
          return false;
        }
        res = await saveScheduleAiPlanShowsService({ ...body, confirmNonCompliant: true });
      }
      if (res?.data?.version !== undefined) planVersion.value = res.data.version;
      ElMessage.success(res?.data?.check?.compliance === "NON_COMPLIANT" ? "非準拠の下書きとして保存しました" : "保存しました");
      // 新規上映回の showId・予測動員を受け取るため取り直す
      await reloadKeeping("shows");
      return true;
    } catch (e) {
      await handleConflict(e);
      await reloadOverlapsIfNeeded(e);
      return false;
    } finally {
      showsSaving.value = false;
    }
  };

  // ---------- AI 生成 ----------
  const generating = ref(false);
  const genError = ref("");
  // 生成失敗の原因になった前提ステップ（"setting" | "movie" | null）
  const genErrorStep = ref(null);
  /**
   * 生成エラーを設定（空文字でクリア）。エラーコードがあれば前提ステップの判定に使う
   */
  const setGenError = (msg, code = null) => {
    genError.value = msg;
    genErrorStep.value = msg ? prereqStepOf(msg, code) : null;
  };
  const genStartedAt = ref(0);
  let pollTimer = null;
  let pollErrors = 0;
  // ポーリング中の期間キー（期間を切り替えたら結果を捨てる）
  let pollKey = "";

  const stopPolling = () => {
    clearTimeout(pollTimer);
    pollTimer = null;
    generating.value = false;
  };

  const pollJob = async (jobId) => {
    const key = pollKey;
    try {
      const res = await getScheduleAiPlanJobService(jobId);
      if (key !== periodKey.value || !generating.value) return;
      pollErrors = 0;
      const status = res?.data?.status;
      if (status === "SUCCESS") {
        jobStorage.remove(key);
        stopPolling();
        ElMessage.success("AIがタイムチャートを作成しました");
        await loadPeriod();
        return;
      }
      if (status === "FAILED") {
        jobStorage.remove(key);
        stopPolling();
        setGenError(res?.data?.message || "タイムチャートの作成に失敗しました");
        // 生成中に別の画面で重なる期間が保存されると FAILED になるため、編集を残したまま重なりの情報を取り直す
        // （次の「ルールを確定」で新しい重なりについて改めて上書きの確認を出す）
        await loadPeriod({ input: inputDirty.value, rules: rulesDirty.value, shows: showsDirty.value });
        return;
      }
    } catch {
      if (key !== periodKey.value || !generating.value) return;
      pollErrors += 1;
      if (pollErrors >= POLL_MAX_ERRORS) {
        stopPolling();
        setGenError("生成状況を取得できませんでした。しばらくしてから再読み込みしてください");
        return;
      }
    }
    pollTimer = setTimeout(() => pollJob(jobId), POLL_INTERVAL);
  };

  const startPolling = (jobId, startedAt = Date.now()) => {
    stopPolling();
    pollKey = periodKey.value;
    pollErrors = 0;
    setGenError("");
    genStartedAt.value = startedAt;
    generating.value = true;
    pollTimer = setTimeout(() => pollJob(jobId), POLL_INTERVAL);
  };

  /**
   * 「ルールを確定」できない理由（空文字なら確定可）。確定すると続けて AI 生成するため生成の前提もここで見る
   */
  const generateBlockReason = computed(() => {
    if (!movies.value.length) return "上映作品が登録されていません";
    if (rulesParsing.value) return "ルールを解析しています";
    if (parseError.value) return "ルールを解析できませんでした。再試行してください";
    return "";
  });

  /**
   * AI 生成ジョブを開始してポーリングする（確認・ルールの保存は confirmRulesAndGenerate が行う）
   */
  const startGenerate = async ({ overwriteOverlap = false } = {}) => {
    setGenError("");
    try {
      const res = await generateScheduleAiPlanService({ ...periodParams(), overwriteOverlap });
      const jobId = res?.data?.jobId;
      if (!jobId) {
        setGenError("生成ジョブを開始できませんでした");
        return;
      }
      jobStorage.set(periodKey.value, `${jobId}|${Date.now()}`);
      startPolling(jobId);
    } catch (e) {
      // 前提条件（劇場設定・上映時間の未登録など）のメッセージを画面に残す
      setGenError(errorText(e, "タイムチャートの作成を開始できませんでした"), e?.code);
      await reloadOverlapsIfNeeded(e);
    }
  };

  /**
   * 「ルールを確定」：既存のタイムチャートがあれば上書き確認 → 排班済みの日と重なれば上書き確認
   * → ルールを保存 → AI 生成を開始（重なる日の上映回は生成が成功したときに上書きされる）
   * 解析できなかったルール（AMBIGUOUS）があっても止めず、確認のうえ続行できる（AI が原文を参考に判断する）
   * 戻り値：生成を開始できたか
   */
  const confirmRulesAndGenerate = async () => {
    if (generateBlockReason.value) {
      ElMessage.warning(generateBlockReason.value);
      return false;
    }
    const ambiguousCount = rules.value.filter((r) => r.resolve === "AMBIGUOUS").length;
    if (ambiguousCount) {
      try {
        await ElMessageBox.confirm(
          `AIが解析できなかった要望が ${ambiguousCount} 件あります。原文を参考にAIが判断しますが、必ず反映されるとは限りません。このまま生成しますか？`,
          "確認",
          { confirmButtonText: "続行", cancelButtonText: "キャンセル", type: "warning" },
        );
      } catch {
        return false;
      }
    }
    if (hasSavedShows.value || showsDirty.value) {
      const extra = showsDirty.value ? "（編集中の未保存の変更も破棄されます）" : "";
      // 排班済みの日とも重なる場合は、確認を 1 回にまとめる
      if (splitMessage.value) {
        ElMessage.error(splitMessage.value);
        return false;
      }
      const overlapExtra = overwriteOverlaps.value.length
        ? `また、保存済みの期間（${overlapLabels.value}）と重なる日の上映回も上書きされます。`
        : "";
      try {
        await ElMessageBox.confirm(`既存のタイムチャートを上書きします${extra}。${overlapExtra}よろしいですか？`, "確認", {
          confirmButtonText: "上書きして作成",
          cancelButtonText: "キャンセル",
          type: "warning",
        });
      } catch {
        return false;
      }
    } else if (!(await confirmOverwriteOverlap("作成"))) {
      return false;
    }
    // ルール確認に入る時点で保存済みのはずだが、念のため未保存の入力があれば先に保存する
    if (needsInputSave() && !(await saveInput({ silent: true }))) return false;
    if (!(await saveRules({ silent: true }))) return false;
    await startGenerate({ overwriteOverlap: overwriteOverlaps.value.length > 0 });
    return generating.value;
  };

  /**
   * ページ再読み込み前に開始していた生成ジョブがあれば追跡を再開
   */
  const resumeJob = () => {
    const stored = jobStorage.get(periodKey.value);
    if (!stored) return;
    const [jobId, startedAt] = stored.split("|");
    if (jobId) startPolling(jobId, Number(startedAt) || Date.now());
  };

  /**
   * 期間を切り替える（未保存の確認・期間の妥当性チェックは呼び出し側）
   */
  const changePeriod = async (from, to) => {
    stopPolling();
    setGenError("");
    parseError.value = "";
    rulesParsing.value = false;
    clearTimeout(validateTimer);
    dateFrom.value = from;
    dateTo.value = to;
    await loadPeriod();
    if (!loadErrorMsg.value) resumeJob();
  };

  // ---------- 期間の削除 ----------
  const deleting = ref(false);

  /**
   * 保存済みの期間を削除（条件設定・ルール・上映回をまとめて削除）。確認は呼び出し側。
   * 成功したら同じ期間を未保存の状態で取り直す。戻り値：削除できたか
   */
  const deletePeriod = async () => {
    if (!saved.value) return false;
    deleting.value = true;
    try {
      await deleteScheduleAiPlanService({ ...periodParams(), version: planVersion.value });
      jobStorage.remove(periodKey.value);
      ElMessage.success("期間を削除しました");
      await changePeriod(dateFrom.value, dateTo.value);
      return true;
    } catch (e) {
      await handleConflict(e);
      return false;
    } finally {
      deleting.value = false;
    }
  };

  const dispose = () => {
    stopPolling();
    clearTimeout(validateTimer);
  };

  return {
    canEdit,
    dateFrom,
    dateTo,
    periodKey,
    days,
    loading,
    loadErrorMsg,
    loadErrorStep,
    saved,
    planVersion,
    generatedTime,
    overlaps,
    overlapMessage,
    splitOverlaps,
    adMinute,
    trailerMinute,
    screens,
    movies,
    targets,
    rules,
    rulesStale,
    shows,
    check,
    violations,
    violationsByShow,
    summary,
    estTotalSeats,
    estUnknownCount,
    hasSavedShows,
    inputDirty,
    textsDirty,
    rulesDirty,
    showsDirty,
    anyDirty,
    screenMap,
    movieMap,
    versionMap,
    ruleMap,
    colorMap,
    inputSaving,
    rulesParsing,
    rulesSaving,
    parseError,
    validating,
    showsSaving,
    generating,
    genError,
    genErrorStep,
    genStartedAt,
    generateBlockReason,
    loadPeriod,
    changePeriod,
    deleting,
    deletePeriod,
    saveInput,
    parseRules,
    saveRules,
    addShow,
    updateShow,
    removeShow,
    discardShows,
    saveShows,
    confirmRulesAndGenerate,
    dispose,
  };
};

/**
 * index.vue で状態を provide する
 */
export const providePlanState = (state) => provide(PLAN_STATE_KEY, state);

/**
 * 子コンポーネントで状態を受け取る
 */
export const usePlanState = () => inject(PLAN_STATE_KEY);
